import Grid from "@material-ui/core/Grid";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import HelpIcon from "@material-ui/icons/Help";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import Tooltip from "@material-ui/core/Tooltip";
import * as H from "helper";
import * as C from "components";

const useStyles = makeStyles((theme) => ({
  content: {
    position: "relative",
    padding: theme.spacing(1) * 2,
    "&:last-child": {
      paddingBottom: theme.spacing(1) * 2,
    },
  },
  icon: {
    boxShadow: "none",
    color: "white",
  },
  iconFloat: {
    position: "absolute",
    right: "15px",
    top: "50%",
    marginTop: "-20px",
    opacity: "0.2",
    transform: "rotate(-5deg)",
  },
  box: {
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(1),
    borderRight: "1px solid #dcdcdc",
  },
  boxLast: {
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(1),
  },
  title: {
    display: "block",
    height: 42,
    fontSize: 14,
  },
  titleTruncate: {
    height: "auto",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  titleContainer: {
    display: "flex",
    alignItems: "flex-start",
  },
  titleText: {
    flex: 1,
    minWidth: 0,
  },
  titleTooltipTarget: {
    width: "100%",
    cursor: "help",
  },
  titleHintButton: {
    padding: 2,
    marginLeft: 4,
    marginTop: -2,
    color: "#7f898b",
  },
  titleHintIcon: {
    fontSize: 14,
  },
  titleMainRow: {
    display: "flex",
    alignItems: "flex-start",
  },
  titleMainText: {
    flex: 1,
    minWidth: 0,
    height: "auto",
  },
  titleMainTextEllipsis: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  titleCountLabel: {
    display: "block",
  },
  value: {
    paddingTop: 0,
    paddingBottom: 0,
    minWidth: 80,
  },
  bar: {
    height: 6,
    width: "80%",
  },
  numSection: {
    display: "block",
  },
  circleProgress: {
    marginTop: 8,
    marginBottom: 0,
  },
  txtCountry: {
    marginTop: -16,
    fontSize: 14,
  },
  txtPrev: {
    marginTop: -8,
    fontSize: 13,
  },
  gainLossImg: {
    paddingTop: 8,
    marginRight: 4,
  },
  diffLbl: {
    color: "#7f898b",
  },
  lineChartLast: {
    marginTop: 16,
    width: "95%",
  },
  iconTooltip: {
    paddingTop: 0,
    paddingRight: 16,
    fontSize: 16,
  },
  currency: {
    fontSize: 14,
  },
  container: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  vsExtra: {
    fontSize: 13,
    marginLeft: 0,
  },
  valueExtra: {
    fontSize: 13,
    marginTop: 8,
    marginLeft: 0,
    marginRight: 12,
  }
}));

const StatBoxSlim = ({
  title,
  value,
  color,
  prevValue,
  tip,
  extra,
  vsExtra,
  prefix,
  lastItem,
  suffix,
  valueExtra,
  truncateTitleAt,
}) => {
  const classes = useStyles();
  const plainTitle =
    typeof title === "string"
      ? title.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim()
      : "";
  const titleMain = plainTitle.replace(/\s*排出件数\s*$/, "").trim();
  const isCountTitle = /\s*排出件数\s*$/.test(plainTitle);
  const isNumericTitleMain = /^[0-9]+$/.test(titleMain);
  const shouldSplitCountLabel = isCountTitle && !isNumericTitleMain;
  const tooltipTitle = titleMain;
  const shouldTruncateTitle =
    Number.isInteger(truncateTitleAt) &&
    truncateTitleAt > 0 &&
    plainTitle.length >= truncateTitleAt;

  const getProgressCircle = (size) => {
    return (
      <div className={classes.circleProgress}>
        <CircularProgress size={size} />
      </div>
    );
  };

  const getPrevComponent = (value, prevValue) => {
    const imageName = value > prevValue ? "gain" : "loss";
    const diffValue = value - prevValue;
    const diffRatio = (diffValue / prevValue) * 100;

    if (value === prevValue) {
      return <div className={classes.txtPrev}>---</div>;
    }

    return (
      <div className={classes.txtPrev}>
        <img
          className={classes.gainLossImg}
          src={`/static/images/${imageName}.png`}
        />
        {H.FormatNumStrHelper.formatCommaThousand(diffValue)}
        {diffRatio !== Infinity ? ` (${Math.abs(diffRatio.toFixed(2))}%)` : null}
      </div>
    );
  };

  const getStatValue = (data, prefix, suffix, vsExtra, valueExtra) => {
    if (data === "-") {
      return "-";
    } else {
      if (prefix === undefined) {
        prefix = "";
      }
      return (
        <div className={classes.container}>
        <Typography variant="h6" className={classes.value}>
          <span className={classes.currency}>{prefix}</span>{" "}
          {H.FormatNumStrHelper.formatCommaThousand(data)} {suffix ?  <Typography variant="caption">{suffix}</Typography> : null}
          {vsExtra &&
            <div className={classes.vsExtra}>{vsExtra}</div>
          }
        </Typography>
        </div>
      );
    }
  };

  const HtmlTooltip = withStyles((theme) => ({
    tooltip: {
      backgroundColor: "#f5f5f9",
      color: "rgba(0, 0, 0, 0.87)",
      maxWidth: 220,
      fontSize: theme.typography.pxToRem(12),
      border: "1px solid #dadde9",
    },
  }))(Tooltip);

  const titleNode = (
    <div className={classes.titleContainer}>
      {shouldSplitCountLabel ? (
        <div className={classes.titleText}>
          <div className={classes.titleMainRow}>
            <Typography
              variant="caption"
              className={`${classes.title} ${classes.titleMainText}${shouldTruncateTitle ? ` ${classes.titleMainTextEllipsis}` : ""}`}
            >
              {titleMain}
            </Typography>
            {shouldTruncateTitle && (
              <IconButton
                size="small"
                className={classes.titleHintButton}
                tabIndex={-1}
                disableRipple
                aria-label="title tooltip"
              >
                <HelpOutlineIcon className={classes.titleHintIcon} />
              </IconButton>
            )}
          </div>
          <Typography variant="caption" className={classes.titleCountLabel}>
            排出件数
          </Typography>
        </div>
      ) : (
        <div className={classes.titleMainRow}>
          <Typography
            variant="caption"
            className={`${classes.title} ${classes.titleText}${shouldTruncateTitle ? ` ${classes.titleTruncate}` : ""}`}
            {...(shouldTruncateTitle
              ? {}
              : { dangerouslySetInnerHTML: { __html: title } })}
          >
            {shouldTruncateTitle ? plainTitle : null}
          </Typography>
          {shouldTruncateTitle && (
            <IconButton
              size="small"
              className={classes.titleHintButton}
              tabIndex={-1}
              disableRipple
              aria-label="title tooltip"
            >
              <HelpOutlineIcon className={classes.titleHintIcon} />
            </IconButton>
          )}
        </div>
      )}
    </div>
  );

  return (
    <Grid
      container
      alignItems={"center"}
      direction={"row"}
      justifyContent={"flex-start"}
    >
      <Grid item xs={12}>
        <div className={lastItem ? classes.boxLast : classes.box}>
          <Grid container direction={"row"} justifyContent={"flex-start"}>
            <Grid item xs={11}>
              {shouldTruncateTitle ? (
                <HtmlTooltip
                  placement="top"
                  title={
                    <Typography color="inherit">{tooltipTitle}</Typography>
                  }
                >
                  <div className={classes.titleTooltipTarget}>{titleNode}</div>
                </HtmlTooltip>
              ) : (
                titleNode
              )}
            </Grid>
            <Grid item xs={1}>
              {tip && (
                <Grid container justifyContent="flex-end">
                  <HtmlTooltip
                    title={
                      <React.Fragment>
                        <Typography color="inherit">
                          <div dangerouslySetInnerHTML={{ __html: tip }}></div>
                        </Typography>
                      </React.Fragment>
                    }
                  >
                    <HelpIcon
                      className={classes.iconTooltip}
                      fontSize="small"
                    />
                  </HtmlTooltip>
                </Grid>
              )}
            </Grid>
          </Grid>
          <div className={classes.numSection}>
            {value === "loading" && (
              <div className={classes.circleProgress}>
                <CircularProgress size={20} />
              </div>
            )}
            {value !== "loading" && typeof value !== "object" && (
              <Typography variant="caption" className={classes.value}>
                {value === "loading"
                  ? getProgressCircle(20)
                  : isNaN(value) ? "--" : getStatValue(value, prefix, suffix, vsExtra, valueExtra)}
              </Typography>
            )}
            {typeof value === "object" && value !== null && value.count !== undefined (
              <Typography variant="caption" className={classes.value}>
                {H.FormatNumStrHelper.formatCommaThousand(value.count)}
              </Typography>
            )}
          </div>
          {prevValue !== undefined
            ? prevValue === "loading"
              ? getProgressCircle(12)
              : isNaN(prevValue)
                ? "--"
                : getPrevComponent(value, prevValue)
            : ""}
          {color !== undefined ? (
            <div
              className={classes.bar}
              style={{ backgroundColor: color }}
            ></div>
          ) : (
            ""
          )}
          {extra !== undefined ? (extra === "loading" ? null : extra) : null}
          {valueExtra &&
            <Typography
              variant="body2"
              className={classes.valueExtra}
              dangerouslySetInnerHTML={{ __html: valueExtra }}
            />
          }
        </div>
      </Grid>
    </Grid>
  );
};

StatBoxSlim.propTypes = {
  title: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object,
  ]),
  color: PropTypes.string,
  truncateTitleAt: PropTypes.number,
};

const mapStateToProps = (state) => ({});

const mapDispatchToProps = (dispatch) => ({});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(null, { withTheme: true })(StatBoxSlim));
